// Auth helpers: token storage + API client for web-portal
// Uses the Axios instance in ./api.ts so every request automatically
// gets the Authorization header and 401s redirect to login.

import { api } from './api';
import type { AxiosError } from 'axios';

export interface TokenPair {
  accessToken:  string;
  refreshToken: string;
  expiresIn:    number;
  tokenType?:   string;
}

export interface AuthUser {
  sub:   string;
  email: string;
  roles: Array<{ role: string; schoolId: string | null }>;
}

// ── localStorage keys ──────────────────────────────────────────────────────
const ACCESS_KEY  = 'serps_access_token';
const REFRESH_KEY = 'serps_refresh_token';
const USER_KEY    = 'serps_user';

// Migrate tokens stored under the old key names (one-time, silent).
(function migrate() {
  try {
    const oldAccess = localStorage.getItem('serps_access');
    if (oldAccess && !localStorage.getItem(ACCESS_KEY)) {
      localStorage.setItem(ACCESS_KEY,  oldAccess);
      localStorage.setItem(REFRESH_KEY, localStorage.getItem('serps_refresh') ?? '');
      localStorage.setItem(USER_KEY,    localStorage.getItem('serps_user') ?? '');
    }
    localStorage.removeItem('serps_access');
    localStorage.removeItem('serps_refresh');
  } catch { /* storage blocked */ }
})();

// ── Token helpers ──────────────────────────────────────────────────────────

export function saveTokens(pair: TokenPair, user: AuthUser): void {
  localStorage.setItem(ACCESS_KEY,  pair.accessToken);
  localStorage.setItem(REFRESH_KEY, pair.refreshToken);
  localStorage.setItem(USER_KEY,    JSON.stringify(user));
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}

export function getStoredUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw) as AuthUser; } catch { return null; }
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
}

// ── Auth flow ──────────────────────────────────────────────────────────────

export async function apiLogin(
  email: string,
  password: string,
): Promise<{ pair: TokenPair; user: AuthUser }> {
  let pair: TokenPair;
  try {
    const res = await api.post<TokenPair>('/api/auth/login', { email, password });
    pair = res.data;
  } catch (err) {
    const msg = extractMessage(err, 'Log masuk gagal. Sila cuba lagi. / Login failed.');
    throw new Error(msg);
  }

  // Fetch profile using the fresh token (before saving so the interceptor
  // doesn't see an expired/missing token during this transient state).
  let user: AuthUser;
  try {
    const profileRes = await api.get<AuthUser>('/api/auth/profile', {
      headers: { Authorization: `Bearer ${pair.accessToken}` },
    });
    user = profileRes.data;
  } catch (err) {
    const msg = extractMessage(err, 'Gagal mendapatkan profil pengguna. / Failed to fetch profile.');
    throw new Error(msg);
  }

  return { pair, user };
}

// ── API helpers (used by every page) ──────────────────────────────────────

export async function apiGet<T>(path: string): Promise<T> {
  try {
    const res = await api.get<T>(path);
    return res.data;
  } catch (err) {
    throw new Error(extractMessage(err, `Ralat memuatkan data. / Error loading data.`));
  }
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  try {
    const res = await api.post<T>(path, body);
    return res.data;
  } catch (err) {
    throw new Error(extractMessage(err, `Ralat menyimpan data. / Error saving data.`));
  }
}

export async function apiPut<T>(path: string, body?: unknown): Promise<T> {
  try {
    const res = await api.put<T>(path, body);
    return res.data;
  } catch (err) {
    throw new Error(extractMessage(err, `Ralat mengemaskini data. / Error updating data.`));
  }
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  try {
    const res = await api.patch<T>(path, body);
    return res.data;
  } catch (err) {
    throw new Error(extractMessage(err, `Ralat mengemaskini data. / Error updating data.`));
  }
}

export async function apiDelete<T>(path: string): Promise<T> {
  try {
    const res = await api.delete<T>(path);
    return res.data;
  } catch (err) {
    throw new Error(extractMessage(err, `Ralat memadam data. / Error deleting data.`));
  }
}

// ── Utility ────────────────────────────────────────────────────────────────

function extractMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'isAxiosError' in err) {
    const ae = err as AxiosError<{ message?: string | string[] }>;
    const msg = ae.response?.data?.message;
    if (typeof msg === 'string' && msg.length > 0) return msg;
    if (Array.isArray(msg) && msg.length > 0) return msg.join('; ');
    if (ae.code === 'ERR_NETWORK' || ae.code === 'ECONNREFUSED') {
      return 'Tidak dapat menghubungi pelayan. / Cannot reach server.';
    }
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function primaryRoleLabel(user: AuthUser, lang: 'ms' | 'en' = 'ms'): string {
  const roles = user.roles.map((r) => r.role);
  if (lang === 'en') {
    if (roles.includes('super_admin')) return 'MOE Officer';
    if (roles.includes('admin'))       return 'School Admin';
    if (roles.includes('principal'))   return 'Principal';
    if (roles.includes('teacher'))     return 'Teacher';
    if (roles.includes('clerk'))       return 'Clerk';
    if (roles.includes('parent'))      return 'Parent / Guardian';
    return 'User';
  }
  if (roles.includes('super_admin')) return 'Pegawai KPM';
  if (roles.includes('admin'))       return 'Pentadbir Sekolah';
  if (roles.includes('principal'))   return 'Pengetua';
  if (roles.includes('teacher'))     return 'Guru';
  if (roles.includes('clerk'))       return 'Kerani';
  if (roles.includes('parent'))      return 'Ibu Bapa / Penjaga';
  return 'Pengguna';
}
